import { Duration } from "luxon";
import React from "react";
import type { Source } from "../../api/useSources";
import { meanInterval } from "../../api/useSources";
import createIOSortType from "../../lib/createIOSortType";
import createGlobalState from "@ui/lib/createGlobalState";
import useSortedData from "../../lib/hooks/useSortedData";
import t from "../../i18n";
import formatDate from "@ui/i18n/formatDate";
import type { SortableValue } from "diagnostics-lib/comparator";
import Table from "@ui/Table";
import type { ColumnMetaData } from "@ui/Table/ColumnMetaData";
import formatNumber from "@ui/i18n/formatNumber";
import type { Project } from "../../api/useProjects";
import { useProjects } from "../../api/useProjects";
import { canUserDo } from "diagnostics-domain/canUserDo";
import type { User } from "../../api/useUser";
import { useModals } from "@ui/lib/hooks/useModal";
import Modal from "@ui/Modal";
import { styled } from "@linaria/react";
import { css } from "@linaria/core";
import Badge from "@ui/Badge";
import { badgeTextOverflow } from "@ui/Badge/_styles/BadgeTextOverflow";
import { getBadgeStableColors } from "@ui/Badge/utils";
import DeleteModalForm from "./DeleteModalForm";
import ButtonIconV2 from "@ui/ButtonIconV2";
import { DeleteBinLine, FileCopyLine, CalendarEventLine } from "@ui/IconV2/react";
import DuplicateModalForm from "./DuplicateModalForm";
import Ellipsis from "@ui/Ellipsis";
import GroupsModalContent from "./GroupsModalContent";

const columns = [
  "name",
  "sensorCount",
  "snapshotCount",
  "firstDate",
  "lastDate",
  "meanInterval",
  "linkedProjects",
] as const;
export type Column = (typeof columns)[number];

const getLabel = (column: Column) => {
  switch (column) {
    case "name":
      return t("sources.table.column.name");
    case "sensorCount":
      return t("sources.table.column.sensorCount");
    case "snapshotCount":
      return t("sources.table.column.snapshotCount");
    case "firstDate":
      return t("sources.table.column.firstDate");
    case "lastDate":
      return t("sources.table.column.lastDate");
    case "meanInterval":
      return t("sources.table.column.meanInterval");
    case "linkedProjects":
      return t("sources.table.column.linkedProjects");
    default:
      throw new Error(column satisfies never);
  }
};

type SourceWithProjects = Source & { linkedProjects: Project[] };

const computeRowKey = (source: SourceWithProjects) => source.id;

const linkedProjectsToString = (source: SourceWithProjects) =>
  source.linkedProjects
    .map(p => p.name)
    .sort((a, b) => a.localeCompare(b))
    .join(", ");

const useSort = createGlobalState({
  storage: "session",
  key: "sourcesSort",
  type: createIOSortType(columns),
  defaultValue: {
    column: "name",
    direction: "asc",
  },
  scoped: false,
});

const StyledActions = styled.div`
  display: none;
  margin-left: auto;
  tr:hover & {
    display: flex;
  }
`;

const constrainedFullModal = css`
  &:not(header) {
    max-width: 1700px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const sortSelector: { [key in Column]: (s: SourceWithProjects) => SortableValue | null } = {
  name: s => s.id,
  sensorCount: s => s._count.sensors,
  snapshotCount: s => s.snapshotCount,
  firstDate: s => s.minSnapshotDate,
  lastDate: s => s.maxSnapshotDate,
  meanInterval: s => meanInterval(s),
  linkedProjects: linkedProjectsToString,
};

interface P {
  sources: Source[];
  loading: boolean;
  user: User;
}

function SourceTable({ sources: rawSources, loading, user }: P) {
  const [sort, setSort] = useSort({});
  const [source, setSource] = React.useState<Source | null>(null);
  const projects = useProjects();
  const sources: SourceWithProjects[] = React.useMemo(
    () =>
      rawSources.map(source => ({
        ...source,
        linkedProjects: projects.filter(p => p.sourceId === source.id),
      })),
    [rawSources, projects],
  );

  const sortedSources = useSortedData(sources, sortSelector[sort.column], sort.direction);
  const hasCRUDSource = canUserDo(user, "ADMIN_SOURCE_WRITE");
  const { closeModal, modal, openModal } = useModals<"duplicate" | "delete" | "groups">();

  const openDeleteModal = React.useCallback(
    (source: Source) => (e: React.MouseEvent) => {
      e.stopPropagation();
      setSource(source);
      openModal("delete");
    },
    [openModal],
  );

  const openDuplicateModal = React.useCallback(
    (source: Source) => (e: React.MouseEvent) => {
      e.stopPropagation();
      setSource(source);
      openModal("duplicate");
    },
    [openModal],
  );

  const openGroupsModal = React.useCallback(
    (source: Source) => (e: React.MouseEvent) => {
      e.stopPropagation();
      setSource(source);
      openModal("groups");
    },
    [openModal],
  );

  const metadata = React.useMemo<{ [key in Column]: ColumnMetaData<SourceWithProjects> }>(
    () => ({
      name: {
        label: getLabel("name"),
        render: s => (
          <>
            <Ellipsis>{s.id}</Ellipsis>
            <StyledActions>
              {hasCRUDSource ? (
                <>
                  <ButtonIconV2 title={t("sources.groups.link")} onClick={openGroupsModal(s)}>
                    <CalendarEventLine size="m" />
                  </ButtonIconV2>
                  <ButtonIconV2
                    title={t("sources.duplicate.title")}
                    onClick={openDuplicateModal(s)}
                  >
                    <FileCopyLine size="m" />
                  </ButtonIconV2>
                  <ButtonIconV2
                    title={
                      s.linkedProjects.length
                        ? t("sources.delete.forbidden.title")
                        : t("sources.delete.title")
                    }
                    onClick={openDeleteModal(s)}
                    disabled={s.linkedProjects.length > 0}
                  >
                    <DeleteBinLine size="m" />
                  </ButtonIconV2>
                </>
              ) : null}
            </StyledActions>
          </>
        ),
        sticky: true,
        stickyBorder: true,
        minWidth: "200px",
        sortable: true,
      },
      sensorCount: {
        label: getLabel("sensorCount"),
        render: s => formatNumber(s._count.sensors),
        width: "140px",
        sortable: true,
      },
      snapshotCount: {
        label: getLabel("snapshotCount"),
        render: s => formatNumber(s.snapshotCount),
        width: "140px",
        sortable: true,
        showEmptyMark: false,
      },
      firstDate: {
        label: getLabel("firstDate"),
        render: s => (s.minSnapshotDate ? formatDate(s.minSnapshotDate, "date", null) : null),
        width: "140px",
        sortable: true,
      },
      lastDate: {
        label: getLabel("lastDate"),
        render: s => (s.maxSnapshotDate ? formatDate(s.maxSnapshotDate, "datetime", null) : null),
        width: "180px",
        sortable: true,
      },
      meanInterval: {
        label: getLabel("meanInterval"),
        render: s => {
          const duration = Duration.fromMillis(meanInterval(s));
          const durationObject = duration.shiftTo("days", "hours", "minutes").toObject();

          return t("sources.table.column.meanInterval.format", {
            days: durationObject.days ?? 0,
            hours: durationObject.hours ?? 0,
            minutes: durationObject.minutes ?? 0,
          });
        },
        width: "140px",
        sortable: true,
      },
      linkedProjects: {
        label: getLabel("linkedProjects"),
        ellipsis: true,
        render: linkedProjectsToString,
        sortable: false,
        minWidth: "250px",
      },
    }),
    [hasCRUDSource, openDeleteModal, openDuplicateModal, openGroupsModal],
  );
  const groupsModalTitle = source ? (
    <GroupsModalTitle>
      <span>{t("sources.groups.modal.title")}</span>
      <Badge
        title={source.id}
        className={badgeTextOverflow}
        style={getBadgeStableColors(source.id)}
      >
        <Ellipsis>{source.id}</Ellipsis>
      </Badge>
    </GroupsModalTitle>
  ) : (
    t("sources.groups.modal.title")
  );

  return (
    <>
      <Modal
        title={t("sources.delete.title")}
        isOpen={modal === "delete"}
        onRequestClose={closeModal}
      >
        {source ? <DeleteModalForm onRequestClose={closeModal} source={source} /> : null}
      </Modal>
      <Modal
        title={t("sources.duplicate.title")}
        isOpen={modal === "duplicate"}
        onRequestClose={closeModal}
        size="l"
      >
        {source ? <DuplicateModalForm onRequestClose={closeModal} source={source} /> : null}
      </Modal>
      <Modal
        title={groupsModalTitle}
        isOpen={modal === "groups"}
        onRequestClose={closeModal}
        size="full"
        className={constrainedFullModal}
      >
        {source ? <GroupsModalContent source={source} isOpen={modal === "groups"} /> : null}
      </Modal>

      <Table
        stickyHeader
        loading={loading}
        datas={sortedSources}
        metadata={metadata}
        computeRowKey={computeRowKey}
        sort={sort}
        onSort={setSort}
      />
    </>
  );
}

export default React.memo(SourceTable);

const GroupsModalTitle = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;
